import { reactive } from 'vue'
import { getAuthInstance } from '@/firebaseInit'
import { loadFirestore } from '@/utils/loadFirestore'
import { artistConfig } from '@/config/artist.config'
import { getTenantUid, setDevTenantUid, clearDevTenantUid, extractSubdomain, hostnameDocToHost } from '@/utils/tenantUtils'
import { restGetDocFields, restFieldsToPlain, restRunQuery, restCountQuery, restNumberValue, restGetSiteBundle, refreshSiteBundle, restConsumePrefetchedWorks } from '@/utils/firestoreRest'
import { mark, summary } from '@/utils/perfMarks'
import { logClientEvent } from '@/utils/clientLog'
import { isVerifiedSession } from '@/utils/loginSession'
import { injectImagePreloads, computePreloadImages } from '@/utils/preloadImages'
import { loadFontPair } from '@/utils/loadFontPreset'

const emptyUser = {
  uid: null,
  email: null,
  displayName: null,
  isAdmin: false,
}

function loadCachedUser() {
  try {
    const raw = localStorage.getItem('gallery_user')
    if (!raw) return { ...emptyUser }
    const cached = JSON.parse(raw)
    return {
      ...emptyUser,
      uid: cached.uid || null,
      email: cached.email || null,
      displayName: cached.displayName || null,
      isAdmin: !!localStorage.getItem('gallery_isAdmin'),
    }
  } catch {
    return { ...emptyUser }
  }
}

// Synchronously pre-populate artistConfig from localStorage cache to avoid
// a flash before async tenant/auth resolution completes.
// Priority:
//   1. Hostname subdomain → tenant:host:{subdomain} cache (visitors on tenant subdomains)
//   2. gallery_devTenantUid (admin on platform host)
function preloadArtistConfigFromCache() {
  try {
    let uid = null
    try {
      const subdomain = extractSubdomain(window.location.hostname)
      if (subdomain) {
        const entry = localStorage.getItem(`tenant:host:${subdomain}`)
        uid = entry ? JSON.parse(entry)?.uid : null
      }
    } catch (err) { console.error('preloadArtistConfigFromCache: hostname lookup failed', err) }
    if (!uid) uid = localStorage.getItem('gallery_devTenantUid')
    if (!uid) return null
    const raw = localStorage.getItem(`gallery_${uid}_config`)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const state = reactive({
  works: [],
  allWorks: [],
  worksReady: false,       // true after first batch of works is loaded and rendered
  worksFullyLoaded: false, // true after all works are loaded in background
  totalVisibleWorks: null, // from getCountFromServer(), arrives before background load completes
  user: loadCachedUser(),
  inbox: [],
  artistConfig: preloadArtistConfigFromCache(),
  onboardingCompleted: null,
  openWizardOnMount: false,
  customPages: [],  // Custom pages for navbar
  navigationContext: null,  // For scoped navigation in WorkModal (e.g., from gallery blocks)
  lastClosedWork: null,     // Slug of work closed by user — triggers GalleryBlock scroll
  galleryFilters: {
    search: '',
    tags: {},          // { [groupLabel]: string[] }
    availability: '',
    size: '',          // 'small' | 'medium' | 'large'
    sizeRange: null,   // { minWidth, maxWidth, minHeight, maxHeight }
    filtersOpen: false,
    routePath: null
  }
})

// Promise that resolves once Firebase auth state is first determined
let resolveAuthReady
export const authReadyPromise = new Promise(resolve => {
  resolveAuthReady = resolve
})


// Internal: the active inbox listener, or null when none is attached
let inboxListener = null
// Internal: in-flight pages load, shared so concurrent callers don't duplicate the fetch
let customPagesLoadPromise = null

function stopInboxListener() {
  if (inboxListener) {
    inboxListener.detach?.() // real onSnapshot always returns a detach fn; test mocks may not
    inboxListener = null
  }
}

export const mutations = {
  getInbox() {
    stopInboxListener()
    state.inbox = []
    const tenantUid = getTenantUid()
    if (!tenantUid) return

    // Called once per login (setUser). The SDK loads async, so re-check the admin
    // session still exists before attaching - a sign-out may have happened meanwhile.
    loadFirestore().then(({ db, collection, query, orderBy, onSnapshot }) => {
      if (!state.user.isAdmin) return
      const q = query(collection(db, 'users', tenantUid, 'inbox'), orderBy('date', 'desc'))
      // onSnapshot returns the function that detaches the listener
      inboxListener = {
        detach: onSnapshot(q, (querySnapshot) => {
          state.inbox = querySnapshot.docs.map(m => ({ ...m.data(), id: m.id }))
        }, (err) => {
          console.error('Failed to load inbox:', err)
        })
      }
    })
  },
  setUser(user) {
    if (user) {
      state.user.uid = user.uid
      state.user.email = user.email
      state.user.displayName = user.displayName
      localStorage.setItem('gallery_user', JSON.stringify({ uid: user.uid, email: user.email, displayName: user.displayName }))
      if (state.user.isAdmin) {
        mutations.getInbox()
      }
    } else {
      state.user = { ...emptyUser }
      localStorage.removeItem('gallery_user')
    }
  },
  updateUser(details) {
    let key = Object.keys(details)[0]
    let value = details[key]
    state.user[key] = value
  },
  updateArtistConfig(path, value) {
    if (!state.artistConfig) return

    const keys = path.split('.')
    let obj = state.artistConfig
    for (let i = 0; i < keys.length - 1; i++) {
      if (!obj[keys[i]] || typeof obj[keys[i]] !== 'object') {
        obj[keys[i]] = {}
      }
      obj = obj[keys[i]]
    }
    obj[keys[keys.length - 1]] = value

    const tenantUid = getTenantUid()
    if (tenantUid) cacheUtils.saveToCache(tenantUid, state.artistConfig, new Date())
  },
  mergeArtistConfig(data) {
    if (!state.artistConfig) return
    const newConfig = JSON.parse(JSON.stringify(state.artistConfig))
    deepMerge(newConfig, data)
    state.artistConfig = newConfig
    const tenantUid = getTenantUid()
    if (tenantUid) cacheUtils.saveToCache(tenantUid, newConfig, new Date())
  },
  // Same merge but no cache write - unsaved previews must not survive a reload
  previewArtistConfig(data) {
    if (!state.artistConfig) return
    const newConfig = JSON.parse(JSON.stringify(state.artistConfig))
    deepMerge(newConfig, data)
    state.artistConfig = newConfig
  },
  setIsAdmin(isTruthy) {
    if (isTruthy) {
      state.user.isAdmin = isTruthy
      localStorage.setItem('gallery_isAdmin', isTruthy)
    } else {
      state.user.isAdmin = false
      localStorage.removeItem('gallery_isAdmin')
    }
  },
  setOnboardingCompleted(value) {
    state.onboardingCompleted = value
  },
  requestWizardOpen() {
    state.openWizardOnMount = true
  },
  setCustomPages(pages) {
    state.customPages = pages
  },
  updatePageDraft(pageId, draft) {
    const idx = state.customPages.findIndex(p => p.id === pageId)
    if (idx !== -1) state.customPages[idx] = { ...state.customPages[idx], draft }
  },
  // Navigation context for scoped work navigation (e.g., from gallery blocks)
  setNavigationContext(context) {
    state.navigationContext = context
  },
  clearNavigationContext() {
    state.navigationContext = null
  },
  setGalleryFilters(filters) {
    Object.assign(state.galleryFilters, filters)
  },
  clearGalleryFilters() {
    Object.assign(state.galleryFilters, { search: '', tags: {}, availability: '', size: '', sizeRange: null })
  },
  setLastClosedWork(slug) {
    state.lastClosedWork = slug
  },
  clearLastClosedWork() {
    state.lastClosedWork = null
  }
}

// Cache utilities
// Keyed by tenantUid so different tenants don't share cache on the same device
const getCacheKey = (tenantUid) => `gallery_${tenantUid}_config`
const getCacheTimestampKey = (tenantUid) => `gallery_${tenantUid}_config_timestamp`
const getWorksCacheKey = (tenantUid) => `gallery_${tenantUid}_works`
const getWorkCountCacheKey = (tenantUid) => `gallery_${tenantUid}_works_count`
const getPagesListCacheKey = (tenantUid) => `gallery_${tenantUid}_pages_list`
// Visitor nav/footer projection — separate key so it never overwrites the admin full cache.
const getNavPagesCacheKey = (tenantUid) => `gallery_${tenantUid}_nav_pages`
const getPageCacheKey = (tenantUid, slug) => `gallery_${tenantUid}_page_${slug}`

function deepMerge(target, source) {
  Object.keys(source).forEach(key => {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      if (!target[key]) target[key] = {}
      deepMerge(target[key], source[key])
    } else {
      target[key] = source[key]
    }
  })
}

// JSON.stringify flattens Firestore Timestamps to { seconds, nanoseconds }, losing toDate().
function revivePageTimestamps(page) {
  const revived = { ...page }
  for (const field of ['createdAt', 'updatedAt']) {
    const value = revived[field]
    if (value && typeof value === 'object' && typeof value.seconds === 'number') {
      revived[field] = new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1e6)
    }
  }
  return revived
}

const cacheUtils = {
  // Load config from localStorage cache
  loadFromCache(tenantUid) {
    try {
      const cacheKey = getCacheKey(tenantUid)
      const timestampKey = getCacheTimestampKey(tenantUid)

      const cached = localStorage.getItem(cacheKey)
      const timestamp = localStorage.getItem(timestampKey)

      if (cached && timestamp) {
        return {
          config: JSON.parse(cached),
          timestamp: new Date(timestamp)
        }
      }
    } catch (err) {
      // Corrupted cache — ignore and fetch fresh
    }
    return null
  },

  // Save config to localStorage cache
  saveToCache(tenantUid, config, timestamp = new Date()) {
    try {
      const cacheKey = getCacheKey(tenantUid)
      const timestampKey = getCacheTimestampKey(tenantUid)

      localStorage.setItem(cacheKey, JSON.stringify(config))
      localStorage.setItem(timestampKey, timestamp.toISOString())
    } catch (err) {
      // Cache save failed — non-critical, config still works from memory
    }
  },

  // Clear cache
  clearCache(tenantUid) {
    try {
      const cacheKey = getCacheKey(tenantUid)
      const timestampKey = getCacheTimestampKey(tenantUid)

      localStorage.removeItem(cacheKey)
      localStorage.removeItem(timestampKey)
    } catch (err) {
      // Cache clear failed — non-critical
    }
  },

  loadWorksFromCache(tenantUid) {
    try {
      const raw = localStorage.getItem(getWorksCacheKey(tenantUid))
      if (!raw) return null
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : null
    } catch {
      return null
    }
  },

  saveWorksToCache(tenantUid, works) {
    try {
      localStorage.setItem(getWorksCacheKey(tenantUid), JSON.stringify(works))
    } catch {
      // Non-critical — next load will fetch from Firestore.
    }
  },

  loadPageFromCache(tenantUid, slug) {
    try {
      const raw = localStorage.getItem(getPageCacheKey(tenantUid, slug))
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  },

  savePageToCache(tenantUid, slug, page) {
    try {
      localStorage.setItem(getPageCacheKey(tenantUid, slug), JSON.stringify(page))
    } catch {
      // Non-critical
    }
  },

  loadPagesListFromCache(tenantUid) {
    try {
      const raw = localStorage.getItem(getPagesListCacheKey(tenantUid))
      return raw ? JSON.parse(raw).map(revivePageTimestamps) : null
    } catch {
      return null
    }
  },

  savePagesListToCache(tenantUid, pages) {
    try {
      localStorage.setItem(getPagesListCacheKey(tenantUid), JSON.stringify(pages))
    } catch { /* non-critical */ }
  },

  loadNavPagesFromCache(tenantUid) {
    try {
      const raw = localStorage.getItem(getNavPagesCacheKey(tenantUid))
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  },

  saveNavPagesToCache(tenantUid, pages) {
    try {
      localStorage.setItem(getNavPagesCacheKey(tenantUid), JSON.stringify(pages))
    } catch { /* non-critical */ }
  },

  loadWorkCountFromCache(tenantUid) {
    try {
      const raw = localStorage.getItem(getWorkCountCacheKey(tenantUid))
      return raw !== null ? parseInt(raw, 10) : null
    } catch {
      return null
    }
  },

  saveWorkCountToCache(tenantUid, count) {
    try {
      localStorage.setItem(getWorkCountCacheKey(tenantUid), String(count))
    } catch { /* non-critical */ }
  }
}

export { cacheUtils }

export const actions = {
  _authListenerActive: false,
  async loadArtistConfig(prefetchedDoc = null) {
    mark('config:load:start')
    const defaultConfig = artistConfig
    const tenantUid = getTenantUid()

    // Platform mode — no tenant, no cache, render from static defaults.
    if (!tenantUid) {
      state.artistConfig = { ...defaultConfig }
      mark('config:load:end')
      return state.artistConfig
    }

    let cached = null
    try {
      // Step 1: Load from cache immediately for instant startup
      cached = cacheUtils.loadFromCache(tenantUid)
      if (cached) {
        state.artistConfig = cached.config
      }

      // Prefer prefetched doc (login), then the edge bundle (cold visitor), then REST, then SDK.
      let firestoreConfig = null
      if (prefetchedDoc !== null) {
        firestoreConfig = prefetchedDoc.exists() ? prefetchedDoc.data() : null
      } else {
        const bundle = await restGetSiteBundle()
        if (bundle?.config) {
          firestoreConfig = bundle.config
          // Load fonts + first-paint image preloads now, don't wait for the merge/reactivity chain below.
          const { fontHeading, fontBody } = bundle.config.theme || {}
          if (fontHeading && fontBody) loadFontPair(fontHeading, fontBody)
          injectImagePreloads(bundle.config.preloadImages)
        }
        if (!firestoreConfig) {
          try {
            const fields = await restGetDocFields(`users/${tenantUid}`)
            firestoreConfig = fields ? restFieldsToPlain(fields) : null
          } catch {
            const { db, doc, getDoc } = await loadFirestore()
            const docSnap = await getDoc(doc(db, 'users', tenantUid))
            firestoreConfig = docSnap.exists() ? docSnap.data() : null
          }
        }
      }

      if (firestoreConfig) {
        // SDK gives a Timestamp (.toDate()), the REST mapper gives a Date - accept both.
        const ts = firestoreConfig.updatedAt
        const serverTimestamp = ts?.toDate ? ts.toDate() : (ts instanceof Date ? ts : new Date(0))
        const needsUpdate = !cached || serverTimestamp > cached.timestamp || !cached.config?.subdomain
        state.onboardingCompleted = firestoreConfig.onboardingCompleted === true

        if (needsUpdate) {
          const mergedConfig = {
            subdomain: firestoreConfig.subdomain || null,
            customDomain: firestoreConfig.customDomain || '',
            artist: {
              ...defaultConfig.artist,
              ...firestoreConfig.artist,
              socialMedia: {
                ...defaultConfig.artist?.socialMedia,
                ...firestoreConfig.artist?.socialMedia
              }
            },
            site: { ...defaultConfig.site, ...firestoreConfig.site },
            theme: { ...defaultConfig.theme, ...firestoreConfig.theme },
            gallery: { ...defaultConfig.gallery, ...firestoreConfig.gallery }
          }

          // Update state
          state.artistConfig = mergedConfig

          // Update cache
          cacheUtils.saveToCache(tenantUid, mergedConfig, serverTimestamp)

        }
      } else {
        if (!cached) {
          state.artistConfig = { ...defaultConfig }
          cacheUtils.saveToCache(tenantUid, state.artistConfig, new Date())
        }
      }
    } catch (err) {
      // If we have cache, use it as fallback
      if (cached) {
        state.artistConfig = cached.config
      } else {
        state.artistConfig = { ...defaultConfig }
      }
    }

    mark('config:load:end')
    return state.artistConfig
  },
  initializeWorks({ includeHidden = false } = {}) {
    mark('works:load:start')
    state.works = []
    if (includeHidden) state.allWorks = []  // Admin: always start fresh
    // Visitor: preserve any works already fetched (e.g. by fetchWorkBySlug on direct URL)
    state.worksReady = false
    state.worksFullyLoaded = false
    state.totalVisibleWorks = null

    const tenantUid = getTenantUid()
    if (!tenantUid) {
      state.worksReady = true
      state.worksFullyLoaded = true
      mark('works:load:end')
      return Promise.resolve()
    }

    const sortByPosition = (a, b) => (a.position || 0) - (b.position || 0)

    // Admin: serve from cache immediately, then refresh from Firestore in background
    if (includeHidden) {
      const cached = cacheUtils.loadWorksFromCache(tenantUid)
      if (cached && cached.length > 0) {
        state.allWorks = cached.slice().sort(sortByPosition)
        state.works = state.allWorks.filter(w => w.isWorkShown === true)
        state.worksReady = true
        state.worksFullyLoaded = true
        state.totalVisibleWorks = state.works.length
      }

      return loadFirestore()
        .then(({ db, collection, getDocs }) => getDocs(collection(db, 'users', tenantUid, 'works')))
        .then(snapshot => {
          const all = []
          snapshot.forEach(docSnap => { const d = docSnap.data(); d.id = docSnap.id; all.push(d) })
          all.sort(sortByPosition)
          state.allWorks = all
          state.works = all.filter(w => w.isWorkShown === true)
          state.worksReady = true
          state.worksFullyLoaded = true
          state.totalVisibleWorks = state.works.length
          cacheUtils.saveWorksToCache(tenantUid, all)
          mark('works:load:end')
          // Fresh works in hand: repair the visitor preload hints if they drifted.
          actions.syncPreloadImages()
        })
        .catch(err => {
          state.worksReady = true
          state.worksFullyLoaded = true
          mark('works:load:end')
          throw err
        })
    }

    // Visitor: seed from localStorage immediately so gallery never flashes empty
    const cachedWorks = cacheUtils.loadWorksFromCache(tenantUid)
    if (cachedWorks && cachedWorks.length > 0) {
      const cachedIds = new Set(cachedWorks.map(w => w.id))
      const prefetched = state.allWorks.filter(w => !cachedIds.has(w.id))
      state.allWorks = [...cachedWorks, ...prefetched].sort(sortByPosition)
      state.works = state.allWorks
      state.worksReady = true
    }

    // Visitor: first 24 works now, rest in background. REST-first throughout - the SDK
    // chunk costs ~2.3s LCP on slow connections (perf lab), so SDK = fallback only.
    const restWorksQuery = {
      from: [{ collectionId: 'works' }],
      where: { fieldFilter: { field: { fieldPath: 'isWorkShown' }, op: 'EQUAL', value: { booleanValue: true } } },
      orderBy: [{ field: { fieldPath: 'position' }, direction: 'ASCENDING' }]
    }
    const visitorQuery = ({ db, collection, query, where, orderBy }) =>
      query(collection(db, 'users', tenantUid, 'works'), where('isWorkShown', '==', true), orderBy('position'))

    // Seed count from cache immediately, then update from server
    const cachedCount = cacheUtils.loadWorkCountFromCache(tenantUid)
    if (cachedCount !== null) state.totalVisibleWorks = cachedCount

    restCountQuery(`users/${tenantUid}`, { from: restWorksQuery.from, where: restWorksQuery.where })
      .catch(async () => {
        const sdk = await loadFirestore()
        const snapshot = await sdk.getCountFromServer(visitorQuery(sdk))
        return snapshot.data().count
      })
      .then(count => {
        state.totalVisibleWorks = count
        cacheUtils.saveWorkCountToCache(tenantUid, count)
      })
      .catch(() => {})

    // First batch - inline prefetch first (index.html starts the query at HTML parse
    // time; keep restWorksQuery in sync with it), then REST, then SDK fallback.
    const fetchFirstBatch = async () => {
      const prefetched = await restConsumePrefetchedWorks(tenantUid)
      if (prefetched) return prefetched
      try {
        return await restRunQuery(`users/${tenantUid}`, { ...restWorksQuery, limit: 24 })
      } catch {
        const sdk = await loadFirestore()
        const snapshot = await sdk.getDocs(sdk.query(visitorQuery(sdk), sdk.limit(24)))
        const batch = []
        snapshot.forEach(docSnap => { const d = docSnap.data(); d.id = docSnap.id; batch.push(d) })
        return batch
      }
    }

    return fetchFirstBatch()
      .then(async firstBatch => {
        // Preserve any works pre-fetched before this load (e.g. fetchWorkBySlug on direct URL)
        const batchIds = new Set(firstBatch.map(w => w.id))
        const prefetched = state.allWorks.filter(w => !batchIds.has(w.id))
        state.allWorks = [...firstBatch, ...prefetched]
        state.works = state.allWorks
        state.worksReady = true
        cacheUtils.saveWorksToCache(tenantUid, firstBatch)
        mark('works:load:end')

        if (firstBatch.length < 24) {
          // Fewer than 24 total — already complete
          state.worksFullyLoaded = true
          state.totalVisibleWorks = firstBatch.length
          return
        }

        // Cursor by position value (`before: false` = start AFTER it); equal positions
        // can overlap, hence the batchIds skip.
        const lastPosition = firstBatch[firstBatch.length - 1].position ?? 0

        try {
          let remainderDocs
          try {
            remainderDocs = await restRunQuery(`users/${tenantUid}`, {
              ...restWorksQuery,
              startAt: { values: [restNumberValue(lastPosition)], before: false }
            })
          } catch {
            const sdk = await loadFirestore()
            const snapshot = await sdk.getDocs(sdk.query(visitorQuery(sdk), sdk.startAfter(lastPosition)))
            remainderDocs = []
            snapshot.forEach(docSnap => { const d = docSnap.data(); d.id = docSnap.id; remainderDocs.push(d) })
          }
          const remainder = remainderDocs.filter(d => !batchIds.has(d.id))
          state.allWorks = [...firstBatch, ...remainder]
          state.works = state.allWorks
          state.worksFullyLoaded = true
          cacheUtils.saveWorksToCache(tenantUid, [...firstBatch, ...remainder])
        } catch {
          // Background load failed — gallery still works with the first batch
          state.worksFullyLoaded = true
        }
      })
      .catch(err => {
        state.worksReady = true
        state.worksFullyLoaded = true
        mark('works:load:end')
        throw err
      })
  },
  async fetchWorkBySlug(slug) {
    // Already in store — no fetch needed
    const existing = state.allWorks.find(w => w.slug === slug || w.id === slug)
    if (existing) return existing

    const tenantUid = getTenantUid()
    if (!tenantUid) return null

    try {
      // Try by document id first (slug is used as doc id on upload)
      const { db, collection, doc, getDoc, query, where, limit, getDocs } = await loadFirestore()
      const worksRef = collection(db, 'users', tenantUid, 'works')
      const byId = await getDoc(doc(db, 'users', tenantUid, 'works', slug))
      if (byId.exists()) {
        const work = { ...byId.data(), id: byId.id }
        if (!state.allWorks.find(w => w.id === work.id)) {
          state.allWorks = [...state.allWorks, work]
          state.works = [...state.works, work]
        }
        return work
      }

      // Fall back to slug field query
      const q = query(worksRef, where('slug', '==', slug), limit(1))
      const snapshot = await getDocs(q)
      if (!snapshot.empty) {
        const work = { ...snapshot.docs[0].data(), id: snapshot.docs[0].id }
        if (!state.allWorks.find(w => w.id === work.id)) {
          state.allWorks = [...state.allWorks, work]
          state.works = [...state.works, work]
        }
        return work
      }
    } catch {
      // Network error — return null, router guard will show not-found
    }
    return null
  },
  // navOnly (visitor): REST projection of the 6 nav fields, no blocks, no SDK handshake.
  // Default (admin): full docs via SDK, unchanged. Separate cache per mode.
  loadCustomPages({ navOnly = false } = {}) {
    // Already fetching (e.g. App.vue, VisitorNavbar, and Footer all call this on boot) —
    // return the same promise instead of firing a duplicate Firestore read.
    if (customPagesLoadPromise) return customPagesLoadPromise

    const promise = (async () => {
      try {
        const tenantUid = getTenantUid()
        if (!tenantUid) {
          state.customPages = []
          return
        }

        // Seed from cache immediately so navbar never flashes empty
        const cached = navOnly
          ? cacheUtils.loadNavPagesFromCache(tenantUid)
          : cacheUtils.loadPagesListFromCache(tenantUid)
        if (cached) mutations.setCustomPages(cached)

        let pages
        if (navOnly) {
          // REST projection (nav fields only); SDK getDocs fallback.
          try {
            pages = await restRunQuery(`users/${tenantUid}`, {
              from: [{ collectionId: 'customPages' }],
              select: { fields: [
                { fieldPath: 'title' },
                { fieldPath: 'slug' },
                { fieldPath: 'position' },
                { fieldPath: 'isHidden' },
                { fieldPath: 'showInNav' },
                { fieldPath: 'showInFooter' }
              ] }
            })
          } catch {
            const { db, collection, getDocs } = await loadFirestore()
            const snap = await getDocs(collection(db, 'users', tenantUid, 'customPages'))
            pages = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          }
        } else {
          // Full pages (blocks included) - admin contexts
          const { db, collection, getDocs } = await loadFirestore()
          const snap = await getDocs(collection(db, 'users', tenantUid, 'customPages'))
          pages = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        }

        mutations.setCustomPages(pages)
        if (navOnly) cacheUtils.saveNavPagesToCache(tenantUid, pages)
        else cacheUtils.savePagesListToCache(tenantUid, pages)
      } catch (error) {
        console.error('Error loading custom pages:', error)
        if (!state.customPages.length) state.customPages = []
      }
    })().finally(() => {
      customPagesLoadPromise = null
    })

    customPagesLoadPromise = promise
    return promise
  },
  handleSignOut() {
    stopInboxListener()

    // Wipe all in-memory user state
    state.artistConfig = null
    state.works = []
    state.allWorks = []
    state.worksReady = false
    state.inbox = []
    state.customPages = []
    state.onboardingCompleted = null
    state.openWizardOnMount = false
    state.navigationContext = null
    mutations.clearGalleryFilters()
    mutations.setUser(null)
    mutations.setIsAdmin(false)

    // Clear identity localStorage (performance caches are left intact)
    clearDevTenantUid()
  },
  async authCheckUser() {
    if (actions._authListenerActive) return
    actions._authListenerActive = true
    let _authInitialized = false
    let _lastSignedInUid = null
    // Auth SDK is loaded lazily here — off the visitor critical path.
    const [{ onAuthStateChanged }, auth] = await Promise.all([
      import('firebase/auth'),
      getAuthInstance()
    ])
    onAuthStateChanged(auth, async (user) => {
      if (!user) {
        if (_authInitialized) {
          _lastSignedInUid = state.user.uid  // remember in case of token refresh recovery
          actions.handleSignOut()
        }
        _authInitialized = true
        resolveAuthReady()
        return
      }
      if (!_authInitialized) {
        _authInitialized = true
        try {
          // A session that never got past the login code stays logged out
          if (await isVerifiedSession(user)) await actions.resolveUserContext(user)
        } catch (err) {
          // Page refresh auth check failed — user remains logged out
        }
      } else if (_lastSignedInUid === user.uid) {
        // Same user came back after null — Firebase token refresh cycle. Re-resolve.
        _lastSignedInUid = null
        try {
          if (await isVerifiedSession(user)) await actions.resolveUserContext(user)
        } catch (err) {
          console.error('[auth] token refresh re-resolve failed', err)
        }
      }
      resolveAuthReady()
    })
  },
  async resolveUserContext(user) {
    mark('login:start')
    // Ensure auth token is propagated to Firestore before making auth-gated reads.
    // onAuthStateChanged can fire before the token is fully ready.
    await user.getIdToken()
    mark('login:token')

    const { db, doc, getDoc } = await loadFirestore()
    const tenantDocSnap = await getDoc(doc(db, 'users', user.uid))
    mark('login:identity')

    if (tenantDocSnap.exists()) {
      setDevTenantUid(user.uid)
      mutations.setIsAdmin(true)
      mutations.setUser(user)
      await Promise.all([
        actions.loadArtistConfig(tenantDocSnap),
        actions.initializeWorks({ includeHidden: true })
      ])
      mark('login:data')
      actions.loadCustomPages()
    } else {
      mutations.setIsAdmin(false)
    }
    mark('login:end')
    summary('login chain')
  },
  updateUser(details) {
    mutations.updateUser(details)
  },
  getInbox() {
    mutations.getInbox()
  },
  // Rebuild this tenant's edge bundle after an admin publishes config/home changes, so visitors
  // get the new content without waiting out the cache TTL. Refreshes every live hostname for the
  // tenant (subdomain + custom domain) from the `hostnames` collection. Admin-only, best-effort.
  async refreshLiveSite() {
    try {
      const tenantUid = getTenantUid()
      if (!tenantUid) {
        // Log skips so "fan-out never ran" is distinguishable from "ran and failed".
        console.warn('refreshLiveSite skipped: no tenant uid')
        return
      }
      const auth = await getAuthInstance()
      // currentUser is null until the SDK restores the session - wait, don't race it.
      if (auth.authStateReady) await auth.authStateReady()
      const user = auth.currentUser
      if (!user) {
        console.warn('refreshLiveSite skipped: no signed-in user')
        return
      }
      const token = await user.getIdToken()
      const rows = await restRunQuery('', {
        from: [{ collectionId: 'hostnames' }],
        where: {
          fieldFilter: {
            field: { fieldPath: 'uid' },
            op: 'EQUAL',
            value: { stringValue: tenantUid }
          }
        }
      })
      // Always https + production hosts: the save wrote production Firestore, even from dev.
      const targets = rows.map(r => ({
        host: `https://${hostnameDocToHost(r.id, r.type)}`,
        // A custom domain only serves visitors once Cloudflare reports it active.
        live: r.type !== 'custom' || r.live === true
      }))
      console.info('refreshLiveSite targets:', targets.map(t => t.host).join(', '))
      // Unconfirmed domains are still refreshed (they may have just gone live), but their failures are expected.
      await Promise.all(targets.map(async ({ host, live }) => {
        const outcome = await refreshSiteBundle(token, host)
        if (outcome !== 'ok' && live) logClientEvent('site-refresh', { uid: tenantUid, host, outcome: 'failed', detail: outcome })
      }))
    } catch (err) {
      // Non-fatal: edge falls back to its TTL. Log so we can spot if it's failing often.
      console.warn('refreshLiveSite failed:', err)
      logClientEvent('site-refresh', { uid: getTenantUid() || '', outcome: 'failed', detail: String(err?.message || err) })
    }
  },
  // Repairs users/{uid}.preloadImages (visitor first-paint thumb hints) after works
  // changes. Called from the admin initializeWorks reload; home publish has its own
  // write (CustomPageEditor). Rewrites only on drift - most calls no-op.
  async syncPreloadImages() {
    try {
      const tenantUid = getTenantUid()
      if (!tenantUid) return
      // Store copy when full pages are loaded, else one doc read (navOnly loads strip blocks)
      const { db, doc, getDoc, updateDoc } = await loadFirestore()
      let home = state.customPages.find(p => p.id === '_home')
      if (!Array.isArray(home?.blocks)) {
        const snap = await getDoc(doc(db, 'users', tenantUid, 'customPages', '_home'))
        home = snap.exists() ? snap.data() : null
      }
      if (!Array.isArray(home?.blocks)) return
      const computed = computePreloadImages(home.blocks, state.allWorks)
      const stored = state.artistConfig?.preloadImages
      if (Array.isArray(stored) && stored.join('\n') === computed.join('\n')) return
      await updateDoc(doc(db, 'users', tenantUid), { preloadImages: computed })
      mutations.updateArtistConfig('preloadImages', computed)
      actions.refreshLiveSite()
    } catch (err) {
      // Advisory only - stale hints just warm the wrong URLs.
      console.warn('syncPreloadImages failed:', err)
    }
  }
}

export { state as store }
